import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Clock } from "lucide-react";
import UpiPayCard from "../UpiPayCard";
import { BRAND, errMsg } from "./constants";

function AddManagerForm({ onAdd, extraPrice, isFirst }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); onAdd(f).then((ok) => ok && setF({ name: "", email: "", phone: "", password: "" })); };
  const cls = "rounded-xl border-2 px-2.5 py-2 text-sm w-full";
  return (
    <form onSubmit={submit} data-testid="add-manager-form" className="grid grid-cols-2 gap-2 mt-3">
      <input required placeholder="Manager name" data-testid="manager-name-input" className={cls} value={f.name} onChange={set("name")} />
      <input required placeholder="Phone" data-testid="manager-phone-input" className={cls} value={f.phone} onChange={set("phone")} />
      <input required type="email" placeholder="Login email" data-testid="manager-email-input" className={cls} value={f.email} onChange={set("email")} />
      <input required minLength={6} type="text" placeholder="Set password (min 6)" data-testid="manager-password-input" className={cls} value={f.password} onChange={set("password")} />
      <button data-testid="add-manager-submit" className="col-span-2 rounded-full py-2 text-sm font-bold text-white inline-flex items-center justify-center gap-1" style={{ background: BRAND.green }}>
        <UserPlus className="w-4 h-4" /> {isFirst ? "Add manager — FREE" : `Add extra manager — ₹${extraPrice}`}
      </button>
    </form>
  );
}

function ManagerRow({ m, onRemove, extraPrice }) {
  const active = m.status === "active";
  return (
    <div data-testid={`manager-row-${m.id}`} className="py-3 border-t" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <b>{m.name}</b> {m.free && <span className="chip text-[10px]">FREE</span>}
          <div className="text-xs truncate" style={{ color: BRAND.muted }}>{m.email} · {m.phone}</div>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid={`manager-status-${m.id}`} className="text-[11px] font-bold inline-flex items-center gap-1" style={{ color: active ? BRAND.green : BRAND.orange }}>
            {active ? <ShieldCheck className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {active ? "Active" : "Awaiting payment"}
          </span>
          <button data-testid={`manager-remove-${m.id}`} onClick={() => onRemove(m)} aria-label="Remove manager"><Trash2 className="w-4 h-4 text-red-600" /></button>
        </div>
      </div>
      {!active && <div className="mt-3"><UpiPayCard amount={extraPrice} purpose="homestay_manager" referenceId={m.id} /></div>}
    </div>
  );
}

export function ManagersPanel({ pid, authFetch, API }) {
  const [d, setD] = useState({ items: [], extra_price: 199 });
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/managers` }).then((r) => setD(r.data)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const add = async (body) => {
    try {
      const r = await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/managers`, data: body });
      toast.success(r.data.status === "active" ? "Manager can log in now at /login" : "Manager added — pay to activate");
      load(); return true;
    } catch (e) { toast.error(errMsg(e, "Could not add manager")); return false; }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their login will stop working.`)) return;
    try { await authFetch({ method: "delete", url: `${API}/homestay/managers/${m.id}` }); toast.success("Manager removed"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not remove")); }
  };

  return (
    <div data-testid="managers-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold" style={{ color: BRAND.green }}>Manager logins</div>
      <p className="text-xs mt-0.5" style={{ color: BRAND.muted }}>Managers sign in at the normal HeylpMate login. They can update beds, handle bookings and log payments — they can't see revenue charts or settings. 1 manager free; extra ₹{d.extra_price} each (admin verified).</p>
      <AddManagerForm onAdd={add} extraPrice={d.extra_price} isFirst={d.items.length === 0} />
      <div className="mt-3" data-testid="managers-list">
        {d.items.length === 0 && <div className="text-xs" style={{ color: BRAND.muted }}>No managers yet.</div>}
        {d.items.map((m) => <ManagerRow key={m.id} m={m} onRemove={remove} extraPrice={d.extra_price} />)}
      </div>
    </div>
  );
}
